// Shared enforced-filter controls: the designer-defined filter-set picker
// (with the always-available "No filter (all records)" bypass) plus the
// collapsible per-condition value bar the end user uses to re-range an
// unlocked enforced filter.
//
// Rendered by BOTH the saved-records list (ListView) and the multi-tabular
// entry view (EmbedCaptureTabularPreview) so the enforced filter stays
// visible and adjustable when the viewer switches interfaces — switching to
// multi-tabular no longer makes the filter "disappear". Keeping it in one
// component avoids the two surfaces drifting apart.
//
// The inputs themselves are VisualCriteriaForm — the same criteria form a
// visual or dashboard prompts with. A filter value is a criterion: it is
// typed to its column's data type, picked from a picklist where the column
// has one, and looked up from a master where the designer configured one.
// Having ONE form build them is what keeps the two surfaces honest about
// what a column accepts.

import { useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { AlertTriangle, Check, ChevronDown, Filter, Info, Lock } from 'lucide-react'
import { tw } from '../../testHooks'

import type { SqlModel } from '../types'
import type {
  EnforcedConditionEcho, EnforcedFilterSetEcho, FilterNotice,
} from '../contracts'
import { filterInputKind } from '../types'
import { columnDbName } from '../utils'
import { useTxnRuntimeOptional } from '../context'
import VisualCriteriaForm, {
  type VisualCriteriaPromptVar, type VisualCriteriaRuntime,
} from '../VisualCriteriaForm'

// Keyed by each condition's own slot key (its column when that column carries
// a single condition — see EnforcedConditionEcho.key).
export type OverrideMap = Record<string, { from?: string; to?: string; value?: string }>

// The value slot one condition owns. Two conditions on the SAME column (a date
// range written as `>` and `<`) each get their own, which is what stops them
// writing over each other.
function slotOf(c: EnforcedConditionEcho): string {
  return c.key || c.column
}

// A range is two values in one criterion, and VisualCriteriaForm carries them
// comma-joined (the shape the BI engine already splits). The enforced filter
// keeps from/to apart on the wire, so the two are converted at this boundary
// rather than teaching either side the other's shape.
const RANGE_SEP = ','

function joinRange(from?: string | null, to?: string | null): string {
  const lo = (from || '').trim()
  const hi = (to || '').trim()
  return lo || hi ? `${lo}${RANGE_SEP}${hi}` : ''
}

function splitRange(v: string | undefined): { from: string; to: string } {
  const parts = (v ?? '').split(RANGE_SEP)
  return { from: (parts[0] ?? '').trim(), to: (parts[1] ?? '').trim() }
}

interface Props {
  model: SqlModel
  /** Active set's resolved conditions (the value bar). */
  enforcedFilters?: EnforcedConditionEcho[]
  /** Designer-defined sets for the picker. */
  filterSets?: EnforcedFilterSetEcho[]
  activeFilterSetId?: string | null
  bypassed?: boolean
  busy?: boolean
  /** Non-blocking messages from the conditions' validations. */
  notices?: FilterNotice[]
  /** Apply the user's edited condition values. */
  onApplyFilters?: (overrides: OverrideMap) => void
  /** Switch the active set, or choose "No filter (all records)". */
  onSelectFilterSet?: (sel: { setId?: string; bypass?: boolean }) => void
  // Project folder + object name — only needed to open a condition's
  // reference picker. Omitted ⇒ conditions still render, without the picker.
  project?: string
  objName?: string
}

/**
 * Everything the search scope needs, shared by every layout of the parts:
 * the editable values, the criteria form's prompt vars, open/closed state,
 * and what Apply does. `EnforcedFilterControls` (the Spread sheet's bar) and
 * the list's one-row toolbar (`useEnforcedFilterParts`) both build on it, so
 * the two never disagree about a scope.
 */
function useEnforcedFilterState({
  model, enforcedFilters, filterSets, activeFilterSetId, bypassed,
  onApplyFilters, onSelectFilterSet, project,
}: Props) {
  // The lookup transport, when the host wired one (every real transaction
  // surface does; a bare preview may not).
  const rt = useTxnRuntimeOptional()
  const canLookup = !!rt && !!project

  // Fall back to the model's own column definitions for a response that
  // predates the typed echo — an older server still gets typed inputs.
  const kindByDb = useMemo(() => {
    const m = new Map<string, string>()
    for (const cc of model.columns || []) {
      m.set(columnDbName(cc.column).toLowerCase(), filterInputKind(cc.column))
    }
    return m
  }, [model])

  // Local, editable copy of the conditions' values — seeded from the
  // server-resolved echo and re-seeded whenever it changes.
  const echoSig = useMemo(() => JSON.stringify(enforcedFilters || []), [enforcedFilters])
  const [values, setValues] = useState<Record<string, string>>({})
  useEffect(() => {
    const next: Record<string, string> = {}
    for (const c of enforcedFilters || []) {
      next[slotOf(c)] = c.op === 'between'
        ? joinRange(c.from, c.to)
        : (c.value || '')
    }
    setValues(next)
  }, [echoSig])

  // One criterion per condition: the echo already carries the column's data
  // type, its picklist and its reference picker, so nothing here guesses.
  const promptVars = useMemo<VisualCriteriaPromptVar[]>(
    () => (enforcedFilters || []).map((c) => ({
      name: slotOf(c),
      label: `${c.label}${OP_SUFFIX[c.op] ?? ''}`,
      label_from: c.label_from,
      label_to: c.label_to,
      data_type: c.data_type || kindByDb.get(c.column) || 'text',
      operator: c.op,
      options: c.options,
      locked: c.locked,
      placeholder: c.op === 'in' ? 'Add value…' : '',
      ...(canLookup ? {
        lookup: c.lookup,
        lookup_sql: c.lookup_sql,
        lookup_filter: c.lookup_filter,
        lookup_filter_rules: c.lookup_filter_rules as never,
        lookup_title: c.lookup_title,
        lookup_sql_data_col: c.lookup_sql_data_col,
      } : {}),
    })),
    [enforcedFilters, kindByDb, canLookup],
  )

  const criteriaRuntime = useMemo<VisualCriteriaRuntime>(
    () => (rt ? { lookup: rt.lookup, notify: rt.notify } : NO_LOOKUP_RUNTIME),
    [rt],
  )

  const hasEditableFilter = (enforcedFilters || []).some((c) => !c.locked)

  // The scope picker appears whenever the form declares any filter set and
  // the host wired up selection — even with a single set, since "All records
  // (no filter)" is always offered as a bypass.
  const showSetPicker = !!onSelectFilterSet && !!filterSets && filterSets.length > 0

  // Open or collapsed on arrival: the designer's choice for the active set
  // when they made one, otherwise derived — a set that still needs a value
  // from the user opens with its inputs showing, a set whose values are all
  // resolved opens collapsed with the data already on screen.
  const activeSet = useMemo(
    () => (filterSets || []).find((s) => s.id === activeFilterSetId),
    [filterSets, activeFilterSetId],
  )
  const defaultOpen = useMemo(() => {
    if (typeof activeSet?.expand_inputs === 'boolean') return activeSet.expand_inputs
    return (enforcedFilters || []).some(
      (c) => !c.locked && (c.op === 'between'
        ? !(c.from || '').trim() && !(c.to || '').trim()
        : !(c.value || '').trim()),
    )
  }, [activeSet, enforcedFilters])
  const [filterOpen, setFilterOpen] = useState(defaultOpen)
  // Re-evaluated when the user switches set (or the server re-resolves one) —
  // each set brings its own answer to "does this still need a value?".
  useEffect(() => {
    setFilterOpen(defaultOpen)
  }, [activeFilterSetId, bypassed, defaultOpen])

  const applyFilters = () => {
    const out: OverrideMap = {}
    for (const c of enforcedFilters || []) {
      if (c.locked) continue
      const slot = slotOf(c)
      if (c.op === 'between') {
        const { from, to } = splitRange(values[slot])
        out[slot] = { from, to }
      } else {
        out[slot] = { value: values[slot] || '' }
      }
    }
    onApplyFilters?.(out)
    // Refresh happens via onApplyFilters; collapse the panel so the list is
    // back in view with the new values on the condition chips.
    setFilterOpen(false)
  }
  // Cancel: the values the server resolved, and the panel closed.
  const cancelEdit = () => {
    const next: Record<string, string> = {}
    for (const c of enforcedFilters || []) {
      next[slotOf(c)] = c.op === 'between' ? joinRange(c.from, c.to) : (c.value || '')
    }
    setValues(next)
    setFilterOpen(false)
  }

  return {
    values, setValues, promptVars, criteriaRuntime, hasEditableFilter,
    showSetPicker, activeSet, filterOpen, setFilterOpen, applyFilters, cancelEdit,
    hasValueBar: !!enforcedFilters && enforcedFilters.length > 0,
  }
}

/** How a condition reads on its chip: "Order date: 1 Oct → 30 Oct". */
function chipText(c: EnforcedConditionEcho): string {
  if (c.op === 'between') return `${c.label} : ${c.from || '…'} – ${c.to || '…'}`
  const v = (c.value || '').trim()
  const lab = c.options?.find((o) => String(o.data) === v)?.display
  return `${c.label}${OP_SUFFIX[c.op] ?? ''} : ${lab || v || '—'}`
}

/**
 * The search scope as pieces a toolbar places itself:
 *
 *   scope      — "Showing <set> ▾", its menu = every set + "All records
 *                (no filter)";
 *   chips      — one chip per condition of the active set: editable ones
 *                open the criteria panel, locked ones show a lock;
 *   below      — the scope's notices and, while open, the criteria panel.
 *
 * Any piece is null when it has nothing to show.
 */
export function useEnforcedFilterParts(props: Props): {
  scope: ReactNode; chips: ReactNode; below: ReactNode
} {
  const st = useEnforcedFilterState(props)
  const {
    enforcedFilters, filterSets, activeFilterSetId, bypassed, busy, notices,
    onSelectFilterSet, project, objName,
  } = props
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!menuOpen) return undefined
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false) }
    window.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [menuOpen])

  const scopeName = bypassed
    ? 'All records'
    : (st.activeSet?.name || (filterSets || [])[0]?.name || '')
  const pick = (sel: { setId?: string; bypass?: boolean }) => {
    setMenuOpen(false)
    onSelectFilterSet?.(sel)
  }

  const scope = st.showSetPicker ? (
    <div ref={menuRef} style={{ position: 'relative', flexShrink: 0 }}>
      <button type="button" aria-haspopup="menu" aria-expanded={menuOpen}
              disabled={busy} onClick={() => setMenuOpen((o) => !o)}
              title="Chooses which saved records are loaded"
              {...tw('action', 'scope')}
              style={scopeBtnStyle}>
        <Filter size={14} aria-hidden="true" />
        <span style={{ fontWeight: 400, color: 'var(--color-text-secondary)' }}>Showing</span>
        {scopeName}
        <ChevronDown size={14} aria-hidden="true" />
      </button>
      {menuOpen && (
        <div role="menu" aria-label="Search scope" style={menuStyle}>
          {(filterSets || []).map((s) => {
            const on = !bypassed && s.id === activeFilterSetId
            return (
              <button key={s.id} type="button" role="menuitemradio" aria-checked={on}
                      onClick={() => pick({ setId: s.id })}
                      {...tw('action', `scope-set:${s.id}`)}
                      style={menuItemStyle}>
                <span style={menuMarkStyle}>{on ? <Check size={14} /> : null}</span>
                <span style={{ fontWeight: 600 }}>{s.name}</span>
              </button>
            )
          })}
          <div style={{ height: 1, background: 'var(--color-border)', margin: '4px 6px' }} />
          <button type="button" role="menuitemradio" aria-checked={!!bypassed}
                  onClick={() => pick({ bypass: true })}
                  {...tw('action', 'scope-set:__all__')}
                  style={menuItemStyle}>
            <span style={menuMarkStyle}>{bypassed ? <Check size={14} /> : null}</span>
            <span style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <span style={{ fontWeight: 600 }}>All records (no filter)</span>
              <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                Browse everything, page by page
              </span>
            </span>
          </button>
        </div>
      )}
    </div>
  ) : null

  const chips = st.hasValueBar ? (
    <>
      {(enforcedFilters || []).map((c) => (c.locked ? (
        <span key={slotOf(c)} title="Fixed by the designer"
              {...tw('action', `condition:${slotOf(c)}`, { locked: true })}
              style={{ ...condStyle, ...condLockedStyle }}>
          <Lock size={12} aria-label="Fixed by the designer" /> {chipText(c)}
        </span>
      ) : (
        <button key={slotOf(c)} type="button" aria-expanded={st.filterOpen}
                disabled={busy} onClick={() => st.setFilterOpen((o) => !o)}
                title="Change this condition"
                {...tw('action', `condition:${slotOf(c)}`)}
                style={condStyle}>
          {chipText(c)} <ChevronDown size={13} aria-hidden="true" />
        </button>
      )))}
    </>
  ) : null

  const hasNotices = (notices || []).length > 0
  const below = (hasNotices || (st.hasValueBar && st.filterOpen)) ? (
    <>
      {/* Non-blocking messages from the conditions' own validations. A
          blocking one never reaches here — it refuses the list instead. */}
      {hasNotices && (
        <div style={{ padding: '10px 20px', display: 'grid', gap: 4 }}>
          {notices!.map((n, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8125rem',
              color: n.severity === 'W' ? 'var(--color-warning, #b26a00)'
                : 'var(--color-text-secondary)',
            }}>
              {n.severity === 'W' ? <AlertTriangle size={14} /> : <Info size={14} />}
              {n.message}
            </div>
          ))}
        </div>
      )}
      {st.hasValueBar && st.filterOpen && (
        <div style={{
          margin: '0 20px 14px', padding: 16, borderRadius: 10,
          background: 'var(--color-surface-2, #f0f3f7)', border: '1px solid var(--color-border, #dde3ea)',
          display: 'grid', gap: 12,
        }}>
          <VisualCriteriaForm
            project={project || ''}
            objName={objName || ''}
            promptVars={st.promptVars}
            values={st.values}
            onChange={(name, v) => st.setValues((prev) => ({ ...prev, [name]: v }))}
            onSubmit={st.applyFilters}
            runtime={st.criteriaRuntime}
          />
          {st.hasEditableFilter && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary btn-sm" disabled={busy}
                      onClick={st.applyFilters} {...tw('action', 'scope-apply')}>
                Apply
              </button>
              <button className="btn btn-secondary btn-sm" disabled={busy}
                      onClick={st.cancelEdit} {...tw('action', 'scope-cancel')}>
                Cancel
              </button>
            </div>
          )}
        </div>
      )}
    </>
  ) : null

  return { scope, chips, below }
}

/**
 * The search scope as one bar of its own — the Spread sheet's. The list puts
 * the same pieces into its single toolbar row instead (ListView).
 */
export default function EnforcedFilterControls(props: Props) {
  const { scope, chips, below } = useEnforcedFilterParts(props)
  if (!scope && !chips && !below) return null
  return (
    <div style={{ borderBottom: '1px solid var(--color-border)' }}>
      {(scope || chips) && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          padding: '10px 20px',
        }}>
          {scope}
          {chips}
        </div>
      )}
      {below}
    </div>
  )
}

const scopeBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 8, height: 36, padding: '0 12px',
  borderRadius: 8, border: '1px solid var(--color-border-strong, #c7cfd9)',
  background: 'var(--color-bg-secondary, #ffffff)',
  color: 'var(--color-text, #1c2430)', fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap',
  cursor: 'pointer', fontFamily: 'inherit',
}

const menuStyle: React.CSSProperties = {
  position: 'absolute', top: 42, left: 0, zIndex: 20, minWidth: 280,
  background: 'var(--color-bg-secondary, #ffffff)', border: '1px solid var(--color-border, #dde3ea)', borderRadius: 10,
  boxShadow: '0 10px 30px rgba(20,30,45,.16)', padding: 6,
}

const menuItemStyle: React.CSSProperties = {
  display: 'flex', width: '100%', alignItems: 'flex-start', gap: 10, padding: '9px 10px',
  border: 0, borderRadius: 7, background: 'transparent', textAlign: 'left',
  color: 'var(--color-text, #1c2430)', fontSize: '0.875rem', cursor: 'pointer', fontFamily: 'inherit',
}

const menuMarkStyle: React.CSSProperties = {
  width: 16, flex: 'none', color: 'var(--color-accent)', display: 'inline-flex',
}

const condStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 12px',
  borderRadius: 999, border: '1px solid rgba(0,97,208,0.35)',
  background: 'var(--color-accent-weak, rgba(0,97,208,0.10))', color: 'var(--color-accent, #0061D0)',
  fontSize: '0.8125rem', fontWeight: 500, whiteSpace: 'nowrap', cursor: 'pointer',
  fontFamily: 'inherit',
}

const condLockedStyle: React.CSSProperties = {
  borderColor: 'var(--color-border, #dde3ea)', background: 'var(--color-surface-2, #f0f3f7)',
  color: 'var(--color-text-secondary, #465062)', cursor: 'default',
}

// Stand-in transport for a host that wired none. No prompt var carries lookup
// config in that case (see `canLookup`), so nothing can reach it.
const NO_LOOKUP_RUNTIME: VisualCriteriaRuntime = {
  lookup: () => Promise.reject(new Error(
    'No lookup transport is available for this filter bar.')),
}

// Human label suffix per operator so a non-date condition reads naturally,
// e.g. "Status", "Amount ≥", "Name contains".
const OP_SUFFIX: Record<string, string> = {
  eq: '', neq: ' ≠', in: '', gt: ' >', gte: ' ≥', lt: ' <', lte: ' ≤',
  contains: ' contains', starts_with: ' starts with', between: '',
}
